import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageMap } from '@ngx-pwa/local-storage';
import { AuthService } from 'app/core/auth/auth.service';
import { SalesQuote, QuoteRequest, SalesQuoteLineItem, QuoteRequestStandard, Audit, ApplicationIndustryCode, QuoteRequestLocation, Quote, Client, SalesQuoteProfile, SalesQuoteProfileLineItem, SalesQuoteProfileStandard, Application, ClientAdditionalSite, AdditionalSalesQuoteStandard, AuditorsToSalesQuote } from 'app/shared/models/domain/data.model';
import { PageRequest } from 'app/shared/models/request/page-request.model';
import { PageResponse } from '@iapmoit/library.angular.core';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SalesQuoteProfileView } from '../admin/settings/sales-quote-profile/models/sales-quote-profile-view.model';
import { ApplicationsService } from '../applications/applications.service';
import { ClientToContactView } from '../clients/models/clients.types';
import { StandardProfileProperties, ChildLineItemView, SalesQuoteProfileProperties, SalesQuoteRequestView } from './quote.types';
import { SalesQuoteView } from 'app/shared/models/domain/data-view.model';
@Injectable({
    providedIn: 'root'
})

export class QuoteService {
    baseUrl = environment.apiBaseUrl;
    httpOptions: any = {
        withCredentials: true,
        observe: 'response' as const
    };
    isCustomer: boolean = false;
    isAdmin: boolean = false;
    isAuditor: boolean = false;
    private readonly requestKey: string = 'iapmo-quote-search-request';
    private readonly _quotes: BehaviorSubject<PageResponse<any> | null> = new BehaviorSubject(null);
    private readonly _quoteProfiles: BehaviorSubject<PageResponse<SalesQuoteProfile> | null> = new BehaviorSubject(null);
    private readonly _salesQuoteView: BehaviorSubject<SalesQuoteRequestView | null> = new BehaviorSubject(null);
    private readonly _submitSalesQuoteModifier: BehaviorSubject<boolean | null> = new BehaviorSubject(null);
    private readonly _jumpStep: BehaviorSubject<number | null> = new BehaviorSubject(null);

    constructor(
        private readonly _httpClient: HttpClient
        , private readonly storage: StorageMap
        , private readonly _authService: AuthService
        , private readonly applicationService: ApplicationsService
    ) {
        const { isCustomer, isAdmin, isAuditor } = this._authService.getRoleData();
        this.isCustomer = isCustomer;
        this.isAdmin = isAdmin;
        this.isAuditor = isAuditor;
    }

    get quotes$(): Observable<PageResponse<any> | null> {
        return this._quotes.asObservable();
    }

    get quoteProfiles$(): Observable<PageResponse<SalesQuoteProfile> | null> {
        return this._quoteProfiles.asObservable();
    }

    get salesQuoteView$(): Observable<SalesQuoteRequestView | null> {
        return this._salesQuoteView.asObservable();
    }

    get submitSalesQuoteModifier$(): Observable<boolean | null> {
        return this._submitSalesQuoteModifier.asObservable();
    }

    set submitSalesQuoteModifier(value: boolean | null) {
        this._submitSalesQuoteModifier.next(value);
    }

    get jumpStep$(): Observable<number | null> {
        return this._jumpStep.asObservable();
    }

    set jumpStep(value: number | null) {
        this._jumpStep.next(value);
    }

    generateQuote(request): Observable<any> {
        return this._httpClient.post<any>(this.baseUrl + 'api/workflow/generate-quote', request).pipe(
            map((quotes: any) => {
                return quotes;
            })
        );
    }

    staffApproveQuote(id: string): Observable<boolean> {
        return this._httpClient.put<any>(`${this.baseUrl}api/workflow/staff/accept-quote/${id}`, null).pipe(
            map((result: any) => {
                return true;
            })
        );
    }

    getStandardProfileProperties(): Observable<PageResponse<StandardProfileProperties>> {
        return this._httpClient.get<any>(this.baseUrl + 'api/standard-profile/properties').pipe(
            map((properties: PageResponse<StandardProfileProperties>) => {
                return properties;
            })
        );
    }

    getLegacySalesQuoteView(quoteId: string): Observable<any> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/legacy/quote/${quoteId}`, { params: null })
            .pipe(map((data) => {
                if (data && data.item) {
                    return data.item as Quote;
                }
                return null;
            }));
    }

    getLegacySalesQuoteViewById(quoteId: string): Observable<any> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/view/sales-quote/legacy/${quoteId}`, { params: null })
            .pipe(map((data) => {
                if (data && data.item) {
                    return data.item as Quote;
                }
                return null;
            }));
    }

    getLegacySalesQuoteViewByQuoteRequestId(quoteRequestId: string): Observable<any> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/view/sales-quote/legacy`, {
            params: {
                'find[QuoteRequestId]': quoteRequestId,
                'itemsPerPage': 1,
            }
        })
            .pipe(map((data) => {
                if (data && data.items && data.items.length > 0) {
                    return data.items[0] as Quote;
                }
                return null;
            }));
    }

    deleteLegacySalesQuote(quoteId: string): Observable<Quote> {
        return this._httpClient.delete<any>(`${this.baseUrl}api/data/legacy/quote/${quoteId}`, { params: null })
    }

    deleteSalesQuote(quoteId: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}api/data/sales-quote/${quoteId}`, { params: null })
    }

    getSalesQuoteViewBySalesQuoteId(salesQuoteId: any, isCustomer: boolean = false, getLegalOptions: boolean = true): Observable<any> {
        return forkJoin([this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote/${salesQuoteId}`)])
            .pipe(
                switchMap(([data]: [any]) => {
                    const requestId: string = data?.item?.quoteRequestId;
                    return this.getSalesQuoteView(requestId, isCustomer, salesQuoteId, getLegalOptions);
                }));
    }

    getSalesQuoteViewBySalesQuoteIdForEnhanced(salesQuoteId: any, isCustomer: boolean = false, getLegalOptions: boolean = true): Observable<any> {
        return forkJoin([this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote/${salesQuoteId}`)])
            .pipe(
                switchMap(([data]: [any]) => {
                    const requestId: string = data?.item?.quoteRequestId;
                    return this.getSalesQuoteViewForEnhanced(requestId, isCustomer, salesQuoteId, getLegalOptions);
                }));
    }

    getSalesQuoteViewForEnhanced(requestId: string, isCustomer: boolean = false, salesQuoteId: string = '', getLegalOptions: boolean = true): Observable<SalesQuoteRequestView> {
        const tasks = [
            this.getQuoteRequestById(requestId),
            salesQuoteId ? this.getAdminSalesQuoteById(salesQuoteId) : this.getAdminSalesQuoteByQuoteRequestId(requestId),
            salesQuoteId ? this.getAdminSalesQuoteViewById(salesQuoteId, isCustomer) : this.getAdminSalesQuoteViewByQuoteRequestId(requestId, isCustomer),
        ];
        return forkJoin(tasks)
            .pipe(
                switchMap(([quoteRequest, salesQuote, salesQuoteView]:
                    [QuoteRequest, SalesQuote, SalesQuoteView]) => {
                    if (null == salesQuote) {
                        const view = {
                            quoteRequest: quoteRequest
                            , salesQuoteView: null
                            , salesQuote: null
                            , salesQuoteLineItems: null
                            , parentLineItems: null
                        } as SalesQuoteRequestView;
                        this._salesQuoteView.next(view);
                        return of(view);
                    }
                    return forkJoin([
                        this.getLineItemsBySalesQuoteId(salesQuote?.id, 'auditTypeId'),
                        salesQuote?.salesQuoteProfileId ? this.getSalesQuoteProfileById(salesQuote?.salesQuoteProfileId) : of(null),
                        (salesQuote?.salesQuoteProfileId) ? this.getQuoteStandardProfileItemsById(salesQuote?.salesQuoteProfileId) : of(null),
                    ]).pipe(map(([lineItems, workflowProfileData, profileLineItems]) => {
                        const items = lineItems?.map((row: SalesQuoteLineItem) => {
                            return this.prepareRowObject(row);
                        });
                        const view = {
                            quoteRequest: quoteRequest
                            , salesQuoteView: salesQuoteView
                            , salesQuote: salesQuote
                            , salesQuoteLineItems: items
                            , parentLineItems: lineItems?.filter(x => x?.parentId === null)
                            , workflowProfile: workflowProfileData
                            , salesQuoteProfileLineItem: profileLineItems ?? []
                        } as SalesQuoteRequestView;
                        this._salesQuoteView.next(view);
                        return view;
                    }));
                })
            );
    }

    getSalesQuoteView(requestId: string, isCustomer: boolean = false, salesQuoteId: string = '', getLegalOptions: boolean = true): Observable<SalesQuoteRequestView> {
        const tasks = [
            this.getQuoteRequestById(requestId),
            this.getQuoteRequestStandardsById(requestId),
            this.getQuoteRequestIndustryCodesById(requestId),
            this.applicationService.getQuoteRequestLocations(requestId),
            salesQuoteId ? this.getAdminSalesQuoteById(salesQuoteId) : this.getAdminSalesQuoteByQuoteRequestId(requestId),
            salesQuoteId ? this.getAdminSalesQuoteViewById(salesQuoteId, isCustomer) : this.getAdminSalesQuoteViewByQuoteRequestId(requestId, isCustomer),
            getLegalOptions ? this.getLegalOptions() : of(null),
            this.getLegacySalesQuoteViewByQuoteRequestId(requestId),
            this.applicationService.getQuoteRequestApplications(requestId),
            salesQuoteId ? this.getAdditionalSalesQuoteStandardData(salesQuoteId) : of(null),
            salesQuoteId ? this.getAuditorsToSalesQuoteData(salesQuoteId) : of(null),
        ];
        return forkJoin(tasks)
            .pipe(
                //  use switchMap instead of map when we want to chain extra step(s) in here
                //  it justs to use map on the last observable in the chain.
                switchMap(([quoteRequest, quoteRequestStandards, quoteRequestIndustryCodes, quoteRequestLocations, salesQuote, salesQuoteView, legal, legacyQuote, applications, additionalSalesQuoteStandard, auditorsToSalesQuote]:
                    [QuoteRequest, QuoteRequestStandard[], ApplicationIndustryCode[], QuoteRequestLocation[], SalesQuote, SalesQuoteView, any, Quote, Application[], AdditionalSalesQuoteStandard[], AuditorsToSalesQuote[]]) => {

                    if (null == salesQuote) {
                        const mainSite = quoteRequestLocations?.find(x => x.isMainSite);
                        const otherSites = quoteRequestLocations?.filter(x => !x.isMainSite);
                        const view = {
                            quoteRequest: quoteRequest
                            , quoteRequestStandards: quoteRequestStandards
                            , quoteRequestIndustryCodes: quoteRequestIndustryCodes
                            , quoteRequestLocations: quoteRequestLocations
                            , mainSite: mainSite
                            , otherSites: otherSites
                            , salesQuoteView: null
                            , salesQuote: null
                            , salesQuoteLineItems: null
                            , childrenLineItems: null
                            , parentLineItems: null
                            , audit: null
                            , termsOfService: legal
                            , legacyQuote: legacyQuote
                            , application: applications[0] || []
                            , additionalSalesQuoteStandard: additionalSalesQuoteStandard || []
                            , auditorsToSalesQuote: auditorsToSalesQuote || []
                        } as SalesQuoteRequestView;
                        this._salesQuoteView.next(view);
                        return of(view);
                    }
                    return forkJoin([
                        this.getLineItemsBySalesQuoteId(salesQuote?.id, 'auditTypeId'),
                        salesQuote?.salesQuoteProfileId ? this.getSalesQuoteProfileById(salesQuote?.salesQuoteProfileId) : of(null),
                        this.getQuoteStandardProfileBySlug(salesQuote?.standardProfileSlug),
                        (salesQuote?.clientId) ? this._httpClient.get<PageResponse<Client>>(`${this.baseUrl}api/data/admin/clients/${salesQuote?.clientId}`).pipe(map((response) => { return response.item })) : of(null),
                        (salesQuote?.clientId) ? this._httpClient.get<PageResponse<ClientToContactView>>(`${this.baseUrl}api/data/view/client-contact?find[ClientId]=${salesQuote?.clientId}`).pipe(map((response) => { return response.items })) : of(null),
                        (salesQuote?.clientId) ? this._httpClient.get<PageResponse<ClientAdditionalSite>>(`${this.baseUrl}api/data/admin/client-additional-site?itemsPerPage=100&find[ClientId]=${salesQuote?.clientId}`).pipe(map((response) => { return response.items })) : of(null),
                        (salesQuote?.salesQuoteProfileId) ? this.getQuoteStandardProfileItemsById(salesQuote?.salesQuoteProfileId) : of(null),
                        !salesQuoteId && salesQuote?.id ? this.getAdditionalSalesQuoteStandardData(salesQuote?.id) : of(null),
                        !salesQuoteId && salesQuote?.id ? this.getAuditorsToSalesQuoteData(salesQuote?.id) : of(null),
                    ])
                        .pipe(map(([lineItems, workflowProfileData, standardProfileSlug, client, clientToContacts, clientAdditionalSites, profileLineItems, additionalSalesQuoteStandard1, auditorsToSalesQuote1]) => {
                            const items = this.getOrderedLineItems(lineItems);
                            const mainSite = quoteRequestLocations.find(x => x.isMainSite);
                            const otherSites = quoteRequestLocations.filter(x => !x.isMainSite);
                            const view = {
                                quoteRequest: quoteRequest
                                , quoteRequestStandards: quoteRequestStandards
                                , quoteRequestIndustryCodes: quoteRequestIndustryCodes
                                , quoteRequestLocations: quoteRequestLocations
                                , salesQuoteView: salesQuoteView
                                , salesQuote: salesQuote
                                , salesQuoteLineItems: items
                                , childrenLineItems: lineItems?.filter(x => x?.parentId !== null)
                                , parentLineItems: lineItems?.filter(x => x?.parentId === null)
                                , termsOfService: legal
                                , workflowProfile: workflowProfileData
                                , standardProfile: standardProfileSlug
                                , salesQuoteProfileLineItem: profileLineItems ?? []
                                , client: client
                                , clientToContactsView: clientToContacts
                                , legacyQuote: legacyQuote
                                , application: applications[0] || []
                                , mainSite: mainSite
                                , otherSites: otherSites
                                , clientAdditionalSites: clientAdditionalSites
                                , additionalSalesQuoteStandard: additionalSalesQuoteStandard || additionalSalesQuoteStandard1
                                , auditorsToSalesQuote: auditorsToSalesQuote || auditorsToSalesQuote1
                            } as SalesQuoteRequestView;
                            this._salesQuoteView.next(view);
                            return view;
                        }));
                })
            );
        // }
    }

    getOrderedLineItems(lineItems: SalesQuoteLineItem[]): SalesQuoteLineItem[] {
        return lineItems?.map((row: SalesQuoteLineItem) => {
            return this.prepareRowObject(row);
        });
    }

    prepareRowObject(row: SalesQuoteLineItem): SalesQuoteLineItem {
        const orderedRow = {
            auditTypeId: row?.auditTypeId,
            title: row.title,
            daysOnsite: row.daysOnsite,
        } as SalesQuoteLineItem;
        row = Object.assign(orderedRow, row)
        row.modified = row.modified || new Date().toJSON();
        row.daysOnsite = row.daysOnsite || (row.daysOnsite || row.daysOffsite ? 0 : null)
        row.daysOffsite = row.daysOffsite || (row.daysOnsite || row.daysOffsite ? 0 : null)
        row.unitCost = row.unitCost || 0
        row.totalCost = row.totalCost || 0
        row.quantity = row.quantity || 1 //Quantity is not used so far; setting as 1 to multiply by unitCost
        row.sortOrder = row.sortOrder || 0;
        return row;
    }

    getAllSalesQuoteViews(request, role: string = 'admin'): Observable<PageResponse<SalesQuoteView>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<any>(`${this.baseUrl}api/data/${role}/view/sales-quote`, { params: params }).pipe(map((data) => {
            return data as PageResponse<SalesQuoteView>;
        }));
    }

    getAllLegacySalesQuoteViews(request, role: string = 'admin'): Observable<PageResponse<SalesQuoteView>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<any>(`${this.baseUrl}api/data/${role}/view/sales-quote/legacy`, { params: params }).pipe(map((data) => {
            return data as PageResponse<SalesQuoteView>;
        }));
    }

    saveQuoteUpgrade(request: any): Observable<any> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/view/sales-quote/legacy/upgrade`, request).pipe(map((data: any) => data.item));
    }

    getLegalOptions(): Observable<any> {
        return this._httpClient.get<any>(`${this.baseUrl}api/admin/entity/record-value-view`, {
            params: {
                'find[NodeSlug]': 'terms-of-service'
            }
        }).pipe(map((data) => {
            //  https://stackoverflow.com/a/35092559
            const recordIds = [...new Set(data.items.map(item => item.recordId))];

            if (recordIds?.length) {
                return recordIds.map((recordId) => {

                    const record = { recordId: recordId };

                    data.items.filter(d => d.recordId == recordId)
                        .forEach((field) => {
                            switch (field.fieldDatatype) {
                                case 'text':
                                    record[field.fieldSlug] = field.valText;
                                    break;

                                case 'string':
                                default:
                                    record[field.fieldSlug] = field.valString;
                                    break;
                            }

                        });

                    return record;

                })
            }

            return null;
        }));
    }

    getQuoteProfileBySlug(slug): Observable<PageResponse<SalesQuoteProfileProperties>> {

        return this._httpClient.get<any>(this.baseUrl + 'api/workflow/profiles/' + slug).pipe(
            map((quotes: PageResponse<SalesQuoteProfileProperties>) => {
                return quotes;
            })
        );
    }

    getQuoteStandardProfileBySlug(slug): Observable<PageResponse<SalesQuoteProfileProperties>> {

        return this._httpClient.get<any>(this.baseUrl + 'api/workflow/profiles/standard/' + slug).pipe(
            map((quotes: PageResponse<SalesQuoteProfileProperties>) => {
                return quotes;
            })
        );
    }

    getQuoteStandardProfileItemsById(id): Observable<SalesQuoteProfileLineItem> {

        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/sales-quote-profile-line-items?itemsPerPage=10000&find[SalesQuoteProfileId]=${id}`).pipe(
            map((quotes) => {
                return quotes?.items;
            })
        );
    }

    getAuditBySalesQuoteId(salesQuoteId: string): Observable<Audit> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit`, {
            params: {
                'find[SalesQuoteId]': salesQuoteId
            }
        }).pipe(map((data) => {
            if (data && data.item) {
                return data.item as Audit;
            }
            return null;
        }));
    }

    getSalesQuoteByQuoteRequestId(requestId: string): Observable<SalesQuoteView> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/customer/view/sales-quote`, {
            params: {
                'find[QuoteRequestId]': requestId
            }
        }).pipe(map((data) => {
            if (data && data.items && data.items.length) {
                return data.items[0] as SalesQuoteView;
            }
            return null;
        }));
    }

    getAdminSalesQuoteByQuoteRequestId(requestId: string): Observable<SalesQuote> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote`, {
            params: {
                'find[QuoteRequestId]': requestId
            }
        }).pipe(map((data) => {
            if (data && data.items && data.items.length) {
                return data.items[0] as SalesQuote;
            }
            return null;
        }));
    }

    getAdminSalesQuoteById(id: string): Observable<SalesQuote> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote`, {
            params: {
                'find[Id]': id
            }
        }).pipe(map((data) => {
            if (data && data.items && data.items.length) {
                return data.items[0] as SalesQuote;
            }
            return null;
        }));
    }

    getAdminSalesQuoteViewByQuoteRequestId(requestId: string, isCustomer: boolean = false): Observable<SalesQuoteView> {
        const urlSegment = isCustomer ? 'customer' : 'admin';
        return this._httpClient.get<any>(`${this.baseUrl}api/data/${urlSegment}/view/sales-quote`, {
            params: {
                'find[QuoteRequestId]': requestId
            }
        }).pipe(map((data) => {
            if (data && data.items && data.items.length) {
                return data.items[0] as SalesQuoteView;
            }
            return null;
        }));
    }

    getAdminSalesQuoteViewById(id: string, isCustomer: boolean = false): Observable<SalesQuoteView> {
        const urlSegment = isCustomer ? 'customer' : 'admin';
        return this._httpClient.get<any>(`${this.baseUrl}api/data/${urlSegment}/view/sales-quote`, {
            params: {
                'find[Id]': id
            }
        }).pipe(map((data) => {
            if (data && data.items && data.items.length) {
                return data.items[0] as SalesQuoteView;
            }
            return null;
        }));
    }

    getAdditionalSalesQuoteStandardData(id: string): Observable<AdditionalSalesQuoteStandard[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote-additional-standard`, {
            params: {
                'find[SalesQuoteId]': id,
                'itemsPerPage': 10000,
                'currentPage': 1,
                'sort[Created]': 'asc',
            }
        }).pipe(map((data) => {
            if (data && data.items && data.items.length) {
                return data.items as AdditionalSalesQuoteStandard[];
            }
            return null;
        }));
    }

    getAuditorsToSalesQuoteData(id: string): Observable<AuditorsToSalesQuote[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/auditors-to-sales-quote`, {
            params: {
                'find[SalesQuoteId]': id,
                'itemsPerPage': 10000,
                'currentPage': 1,
                'sort[Created]': 'asc',
            }
        }).pipe(map((data) => {
            if (data?.items?.length) {
                return data.items as AuditorsToSalesQuote[];
            }
            return null;
        }));
    }

    saveSalesQuoteByQuoteRequestId(salesQuoteRequestId: string, salesQuote: any): any {

        return forkJoin([
            this._httpClient.put<PageResponse<SalesQuote>>(`${this.baseUrl}api/data/sales-quote/${salesQuoteRequestId}`, salesQuote)
            , this._httpClient.put(`${this.baseUrl}api/workflow/quote/return/${salesQuoteRequestId}`, salesQuote)
        ]);
    }

    getQuoteRequestById(id: string): Observable<QuoteRequest> {
        return this._httpClient.get<any>(this.baseUrl + 'api/data/admin/quote-request/' + id)
            .pipe(map((data) => {
                return data.item as QuoteRequest;
            }));
    }

    getQuoteRequestByIdPublic(id: string): Observable<QuoteRequest> {
        return this._httpClient.get<any>(this.baseUrl + 'api/data/public/quote-request/' + id)
            .pipe(map((data) => {
                return data.item as QuoteRequest;
            }));
    }

    getQuoteRequestViewById(id: string): Observable<any> {
        return this._httpClient.get<any>(this.baseUrl + 'api/data/quote-request-view?find[Id]=' + id)
            .pipe(map((data) => {
                return data.items[0] || [];
            }));
    }

    getQuoteRequestStandardsById(id: string): Observable<QuoteRequestStandard[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/quote-request-standard`, {
            params: {
                'find[QuoteRequestId]': id,
                'itemsPerPage': 25,
                'sort[Created]': 'asc',
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestStandard[];
        }));
    }

    getQuoteRequestStandardsByIdPublic(id: string): Observable<QuoteRequestStandard[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/public/quote-request-standard`, {
            params: {
                'find[QuoteRequestId]': id,
                'itemsPerPage': 25,
                'sort[Created]': 'asc',
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestStandard[];
        }));
    }

    getQuoteRequestIndustryCodesById(id: string): Observable<ApplicationIndustryCode[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/application-industry-code`, {
            params: {
                'find[QuoteRequestId]': id,
                'itemsPerPage': 25
            }
        }).pipe(map((data) => {
            return data.items as ApplicationIndustryCode[];
        }));
    }

    getLineItemsBySalesQuoteId(salesQuoteId: string, sort: string = 'SortOrder'): Observable<SalesQuoteLineItem[]> {
        const params = {
            'find[SalesQuoteId]': salesQuoteId,
            'itemsPerPage': 10000,
        };
        if (sort) {
            params[`sort[${sort}]`] = 'asc';
        }
        return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote-line-items/get-active-items/${salesQuoteId}`, {
            params: params
        }).pipe(map((data) => {
            return data?.item?.map((rows) => {
                rows.primaryKey = rows.id;
                return rows;
            });
        }));
    }

    getLineItemsBySalesQuoteLineItemId(salesQuoteLineItemId: string): Observable<any> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote-line-items/get-childs/${salesQuoteLineItemId}`, {
            params: {

            }
        }).pipe(map((data) => {
            return data.items;
        }));
    }

    getSalesQuoteStandards(salesQuoteId: string): Observable<SalesQuoteLineItem[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote-line-items`, {
            params: {
                'find[SalesQuoteId]': salesQuoteId,
                'itemsPerPage': 10000,
                'sort[SortOrder]': 'asc',
            }
        }).pipe(map((data) => {
            return data.items as SalesQuoteLineItem[];
        }));
    }

    getSalesQuoteLocations(salesQuoteId: string): Observable<SalesQuoteLineItem[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote-line-items`, {
            params: {
                'find[SalesQuoteId]': salesQuoteId,
                'itemsPerPage': 10000,
                'sort[SortOrder]': 'asc',
            }
        }).pipe(map((data) => {
            return data.items as SalesQuoteLineItem[];
        }));
    }

    getParams(request, params): any {
        params['itemsPerPage'] = request.itemsPerPage?.toString();
        params['currentPage'] = request.currentPage?.toString();

        params[`sort[${request.sort}]`] = request.sortDir;

        if (request.quoteStatusId) {
            params['find[quoteStatusId]'] = request.quoteStatusId;
        }

        if (request.standardId?.length > 0) {
            params['find[standardId:in]'] = request.standardId?.join();
        }

        if (request.priorityClient && request.priorityClient != 2) {
            params['find[priorityClient]'] = request.priorityClient;
        } else if (request.priorityClient == 2) {
            params['find[priorityClient]'] = 0;
        }

        if (request.salesRep) {
            params['orLike[assignedToUserId]'] = parseInt(request.salesRep);
        }

        if (request.clientAcceptedDate) {
            params['find[requestStatus]'] = request.requestStatus;
        }

        if (request.clientId) {
            params['find[clientId]'] = request.clientId;
        }

        if (request.quoteId) {
            params['find[quoteId]'] = request.quoteId;
        }

        if (request.active) {
            params['find[active]'] = request.active;
        }

        if (request['salesQuoteId']) {
            params['find[salesQuoteId]'] = request.salesQuoteId;
        }

        if (request['deleted']) {
            params['find[deletedAt]'] = 'IS NULL';
        }

        return params;
    }

    getChildrenLineItemsDatatable(request: any): Observable<PageResponse<ChildLineItemView>> {
        let params = {};
        params = this.getParams(request, params);
        const httpOptionsLocal = { params: params };

        return forkJoin([
            this._httpClient.get<any>(this.baseUrl + 'api/data/sales-quote-line-items', httpOptionsLocal)
        ])
            .pipe(
                map(([lineItems]: [any]) => {
                    const ret: PageResponse<ChildLineItemView> = new PageResponse<ChildLineItemView>();

                    ret.items = lineItems.items?.filter(item => item.parentId !== null).map((row: SalesQuoteLineItem) => {
                        // will return required fields data
                        return {
                            title: row.title
                            , daysOnsite: row.daysOnsite || (row.daysOnsite || row.daysOffsite ? 0 : '')
                            , daysOffsite: row.daysOffsite || (row.daysOnsite || row.daysOffsite ? 0 : '')
                            , unitCost: row.unitCost || 0
                            , quantity: row.quantity || 1 //Quantity is not used so far; setting as 1 to multiply by unitCost
                            , parentId: row.parentId
                        } as ChildLineItemView;
                    });
                    ret.totalItems = lineItems.page?.totalItems;
                    ret.currentPage = lineItems.page?.currentPage;
                    ret.itemsPerPage = lineItems.page?.itemsPerPage;
                    return ret;
                })
            );
    }

    getQuotes(request): Observable<PageResponse<SalesQuote>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<PageResponse<SalesQuote>>(`${this.baseUrl}api/data/sales-quote`, { params: params }).pipe(
            map((quotes: any) => {
                const response: PageResponse<SalesQuote> = new PageResponse<SalesQuote>();
                response.items = quotes.items;
                response.totalItems = quotes.page.totalItems;
                response.currentPage = quotes.page.currentPage;
                response.itemsPerPage = quotes.page.itemsPerPage;
                return response;
            })
        );
    }

    saveLocalRequest(requestKey, model): Observable<undefined> {
        return this.storage.set(requestKey, model);
    }

    getLocalRequest(requestKey): Observable<any> {
        return this.storage.get(requestKey)
            .pipe(
                map(data => new PageRequest().deserialize(data))
            );
    }

    deleteLocalRequest(requestKey): Observable<undefined> {
        return this.storage.delete(requestKey);
    }

    getLocal(): Observable<any> {
        return this.storage.get(this.requestKey)
            .pipe(
                map(data => new PageRequest().deserialize(data))
            );
    }

    saveLocal(model: PageRequest): Observable<any> {
        return this.storage.set(this.requestKey, model);
    }

    deleteLocal(): Observable<any> {
        return this.storage.delete(this.requestKey);
    }

    getAttachments(request: any): Observable<PageResponse<any>> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<any>(`${this.baseUrl}api/data/quote-attachment`, { params: params })
            .pipe(map((data) => {
                return data as PageResponse<any>;
            }));
    }

    getSalesAttachments(request: any): Observable<PageResponse<any>> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/sales-quote-attachments-view`, { params: params })
            .pipe(map((data) => {
                return data as PageResponse<any>;
            }));
    }

    removeSalesQuoteAttachment(id: any): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/admin/sales-quote-attachments/${id}`);
    }

    updateSalesQuoteStandard(request: any): Observable<any> {
        return this._httpClient.put(`${this.baseUrl}api/data/sales-quote/update-sales-quote-standard`, request);
    }

    getSalesQuoteProfilesList(request: any): Observable<PageResponse<SalesQuoteProfile>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        let roleType = 'admin';
        if (this.isCustomer) {
            roleType = 'customer';
        }
        return this._httpClient.get<PageResponse<SalesQuoteProfile>>(this.baseUrl + 'api/data/' + roleType + '/sales-quote-profile', httpOptionsLocal)
            .pipe(map((response: any) => {
                const ret: PageResponse<SalesQuoteProfile> = new PageResponse<SalesQuoteProfile>();
                ret.items = response?.items;
                ret.totalItems = response?.page?.totalItems;
                ret.currentPage = request.currentPage;
                ret.itemsPerPage = request.itemsPerPage;
                this._quoteProfiles.next(ret);
                return ret;
            }));
    }

    getSalesQuoteProfileById(id: string): Observable<SalesQuoteProfile> {
        let roleType = 'admin';
        if (this.isCustomer) {
            roleType = 'customer';
        }
        return this._httpClient.get<PageResponse<SalesQuoteProfile>>(`${this.baseUrl}api/data/${roleType}/sales-quote-profile/${id}`, this.httpOptions).
            pipe(map((response: any) => {
                return response?.body?.item;
            }));
    }

    getSalesQuoteProfileLineItemsByProfileId(salesQuoteProfileId: string): Observable<SalesQuoteProfileLineItem[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/sales-quote-profile-line-items`, {
            params: {
                'find[SalesQuoteProfileId]': salesQuoteProfileId,
                'itemsPerPage': 10000,
                'sort[SortOrder]': 'asc',
            }
        }).pipe(map((data) => {
            return data.items?.map((rows) => {
                return rows;
            });
        }));
    }

    saveAdditionalSalesQuoteStandardData(id = null, payload = {} as any): Observable<PageResponse<any>> {
        payload.standardId = parseInt(payload.standardId);
        if (payload.standardId != '' || payload.standardId == 0) {
            if (id != null && payload.standardId == 0) {
                return this._httpClient.delete<any>(`${this.baseUrl}api/data/sales-quote-additional-standard/${id}`, {});
            }
            else if (id && payload.standardId != 0) {
                return this._httpClient.put<PageResponse<any>>(`${this.baseUrl}api/data/sales-quote-additional-standard/${id}`, payload);
            } else if (payload.standardId != 0) {
                return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/sales-quote-additional-standard`, payload);
            }
        }
        return of(null);
    }

    getSalesQuoteProfileStandardsByProfileId(salesQuoteProfileId: string): Observable<SalesQuoteProfileStandard[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/sales-quote-profile-standard`, {
            params: {
                'find[SalesQuoteProfileId]': salesQuoteProfileId,
                'itemsPerPage': 10000
            }
        }).pipe(map((data) => {
            return data.items?.map(rows => rows);
        }));
    }

    getSalesQuoteProfilesView(id: string): Observable<SalesQuoteProfileView> {
        const tasks = [
            this.getSalesQuoteProfileById(id),
            this.getSalesQuoteProfileLineItemsByProfileId(id),
            this.getSalesQuoteProfileStandardsByProfileId(id)
        ];
        return forkJoin(tasks)
            .pipe(
                switchMap(([salesQuoteProfile, salesQuoteProfileLineItems, salesQuoteProfileStandard]:
                    [SalesQuoteProfile, SalesQuoteProfileLineItem[], SalesQuoteProfileStandard[]]) => {
                    return of({
                        salesQuoteProfile: salesQuoteProfile,
                        salesQuoteProfileLineItems: salesQuoteProfileLineItems,
                        salesQuoteProfileStandard: salesQuoteProfileStandard,
                        parentLineItems: salesQuoteProfileLineItems.filter(p => p.parentId == null),
                        childrenLineItems: salesQuoteProfileLineItems.filter(p => p.parentId != null)
                    } as SalesQuoteProfileView);
                })
            );
    }

    getSalesQuoteProfilesViewOnlyChildren(currentView: SalesQuoteProfileView): Observable<SalesQuoteProfileView> {
        const tasks = [
            this.getSalesQuoteProfileLineItemsByProfileId(currentView?.salesQuoteProfile?.id),
        ];
        return forkJoin(tasks)
            .pipe(
                switchMap(([salesQuoteProfileLineItems]:
                    [SalesQuoteProfileLineItem[]]) => {
                    return of({
                        salesQuoteProfile: currentView.salesQuoteProfile,
                        salesQuoteProfileLineItems: salesQuoteProfileLineItems,
                        salesQuoteProfileStandard: currentView.salesQuoteProfileStandard,
                        parentLineItems: salesQuoteProfileLineItems.filter(p => p.parentId == null),
                        childrenLineItems: salesQuoteProfileLineItems.filter(p => p.parentId != null)
                    } as SalesQuoteProfileView);
                })
            );
    }

    deleteSalesQuoteProfileLineItem(id: string): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/admin/sales-quote-profile-line-items/${id}`, this.httpOptions);
    }

    deleteSalesQuoteProfile(id: string): Observable<any> {
        let roleType = 'admin';
        if (this.isCustomer) {
            roleType = 'customer';
        }
        return this._httpClient.delete(`${this.baseUrl}api/data/${roleType}/sales-quote-profile/${id}`, this.httpOptions);
    }

    moveUpSalesQuoteProfileLineItem(id: string): Observable<boolean> {
        return this._httpClient.put(`${this.baseUrl}api/data/admin/sales-quote-profile-line-items/${id}/move-up`, {}, this.httpOptions)
            .pipe(map((response: any) => {
                return response?.body?.item;
            }));
    }

    moveDownSalesQuoteProfileLineItem(id: string): Observable<boolean> {
        return this._httpClient.put(`${this.baseUrl}api/data/admin/sales-quote-profile-line-items/${id}/move-up`, {}, this.httpOptions)
            .pipe(map((response: any) => {
                return response?.body?.item;
            }));
    }

    updateSalesQuoteProfile(salesQuoteProfile: SalesQuoteProfile): Observable<SalesQuoteProfile> {
        let roleType = 'admin';
        if (this.isCustomer) {
            roleType = 'customer';
        }
        return this._httpClient.put(`${this.baseUrl}api/data/${roleType}/sales-quote-profile/${salesQuoteProfile.id}`, salesQuoteProfile, this.httpOptions)
            .pipe(map((response: any) => {
                return response?.body?.item;
            }));
    }

    createSalesQuoteProfile(salesQuoteProfile: SalesQuoteProfile): Observable<SalesQuoteProfile> {
        let roleType = 'admin';
        if (this.isCustomer) {
            roleType = 'customer';
        }
        return this._httpClient.put(`${this.baseUrl}api/data/${roleType}/sales-quote-profile/`, salesQuoteProfile, this.httpOptions)
            .pipe(map((response: any) => {
                return response?.body?.item;
            }));
    }

    updateSalesQuoteLineItem(id: string, payload: SalesQuoteLineItem): Observable<any> {
        return this._httpClient.patch<any>(`${this.baseUrl}api/data/sales-quote-line-items/${id}`, payload);
    }
}
